import { SlideRef, Tab, TabsState } from "./types";
import { createInitialTabsState } from "./services/storageService";

export interface SlideSelectionEvaluation {
  currentSlideKey: string;
  /** True only on the very first observation in this session (no previous selection recorded). */
  isInitialSelection: boolean;
  /**
   * True whenever the caller should align the active chat tab with the current slide —
   * either because the slide just changed, or because this is the first observation
   * and we need to catch up to whatever slide PowerPoint already had selected.
   */
  shouldAdoptSlide: boolean;
  /**
   * @deprecated Use `shouldAdoptSlide`. Kept for back-compat with callers/tests that
   * only wanted "real slide change" (excludes first observation).
   */
  shouldAutoAddTab: boolean;
}

export interface TabClickResolution {
  nextState: TabsState;
  navigationTarget?: {
    slideId: string;
    slideIndex: number;
  };
}

export function sortTabsBySlideIndex(tabs: Tab[]): Tab[] {
  return [...tabs].sort((a, b) => a.session.slideIndex - b.session.slideIndex);
}

export function getSlideSelectionKey(slideRef: SlideRef): string {
  return slideRef.id || `index:${slideRef.index}`;
}

function getSlideNumber(slide: { index: number; slideNumber?: number }, fallback?: number): number {
  return slide.slideNumber ?? fallback ?? slide.index + 1;
}

function getSlideName(slide: { index: number; slideNumber?: number }): string {
  return `Slide ${getSlideNumber(slide)}`;
}

function tabCarriesUserVisibleHistory(tab: Tab): boolean {
  return (
    tab.session.messages.length > 0 ||
    tab.isLoading ||
    Boolean(tab.loadingStatus) ||
    Boolean(tab.streamingThinking) ||
    Boolean(tab.agentTimeline?.hops.length)
  );
}

function parseSlideSelectionKey(selectionKey: string): { id?: string; index?: number } {
  if (selectionKey.startsWith("index:")) {
    const index = Number.parseInt(selectionKey.slice("index:".length), 10);
    if (Number.isFinite(index)) {
      return { index };
    }
    return {};
  }
  return { id: selectionKey };
}

export function evaluateSlideSelectionChange(
  lastSelectedSlideKey: string | null,
  currentSlide: SlideRef
): SlideSelectionEvaluation {
  const currentSlideKey = getSlideSelectionKey(currentSlide);
  const isInitialSelection = lastSelectedSlideKey === null;

  let isSameSelection = false;
  if (!isInitialSelection && lastSelectedSlideKey) {
    const previous = parseSlideSelectionKey(lastSelectedSlideKey);
    isSameSelection =
      (Boolean(previous.id) && Boolean(currentSlide.id) && previous.id === currentSlide.id) ||
      (typeof previous.index === "number" && previous.index === currentSlide.index);
  }

  const shouldAutoAddTab = !isInitialSelection && !isSameSelection;
  const shouldAdoptSlide = isInitialSelection || shouldAutoAddTab;

  return {
    currentSlideKey,
    isInitialSelection,
    shouldAdoptSlide,
    shouldAutoAddTab,
  };
}

/**
 * First-load helper: when the only tab in state is an unused placeholder
 * (no messages), promote it in place to point at the slide PowerPoint is
 * actually showing. Returns null when state already has real tabs (or the
 * placeholder already matches), in which case the caller should fall back
 * to the regular `addTab` flow so existing matching tabs get reused.
 */
export function adoptInitialSlideSelection(
  state: TabsState,
  currentSlide: SlideRef
): TabsState | null {
  if (state.tabs.length !== 1) return null;
  const onlyTab = state.tabs[0];
  if (onlyTab.session.messages.length !== 0) return null;

  const nextSlideNumber = getSlideNumber(
    currentSlide,
    onlyTab.session.slideIndex === currentSlide.index ? onlyTab.session.slideNumber : undefined
  );
  const nextName = `Slide ${nextSlideNumber}`;
  const alreadyMatches =
    onlyTab.session.slideId === currentSlide.id &&
    onlyTab.session.slideIndex === currentSlide.index &&
    onlyTab.session.slideNumber === nextSlideNumber &&
    onlyTab.session.name === nextName;
  if (alreadyMatches) return null;

  return {
    ...state,
    activeTabId: onlyTab.id,
    tabs: [
      {
        ...onlyTab,
        session: {
          ...onlyTab.session,
          slideId: currentSlide.id,
          slideIndex: currentSlide.index,
          slideNumber: nextSlideNumber,
          name: nextName,
          updatedAt: Date.now(),
        },
      },
    ],
  };
}

export function reconcileTabsStateWithSlides(
  prev: TabsState,
  slides: ReadonlyArray<{ id: string; index: number; slideNumber?: number }>
): TabsState {
  const slideById = new Map(slides.map((slide) => [slide.id, slide]));
  const findReplacementSlideForTab = (tab: Tab) =>
    slides.find((slide) => slide.index === tab.session.slideIndex) ??
    slides.find(
      (slide) =>
        typeof tab.session.slideNumber === "number" &&
        getSlideNumber(slide) === tab.session.slideNumber
    ) ??
    slides[tab.session.slideIndex] ??
    slides[0];

  const reconciledTabs = prev.tabs.flatMap((tab) => {
    const hasSlideId = Boolean(tab.session.slideId);
    let matchingSlide: { id: string; index: number; slideNumber?: number } | undefined;
    if (hasSlideId) {
      matchingSlide = slideById.get(tab.session.slideId);
      if (!matchingSlide && tabCarriesUserVisibleHistory(tab)) {
        matchingSlide = findReplacementSlideForTab(tab);
      }
      if (!matchingSlide) {
        return [];
      }
    } else {
      matchingSlide = findReplacementSlideForTab(tab);
      if (!matchingSlide) {
        return [];
      }
    }

    return [
      {
        ...tab,
        session: {
          ...tab.session,
          slideId: matchingSlide.id,
          slideIndex: matchingSlide.index,
          slideNumber: getSlideNumber(matchingSlide),
          name: getSlideName(matchingSlide),
        },
      },
    ];
  });

  let nextTabs = sortTabsBySlideIndex(reconciledTabs);
  if (nextTabs.length === 0) {
    const fallbackTab = prev.tabs[0] ?? createInitialTabsState().tabs[0];
    const firstSlideId = slides[0]?.id || "";
    const firstSlideNumber = slides[0] ? getSlideNumber(slides[0]) : 1;
    nextTabs = [
      {
        ...fallbackTab,
        session: {
          ...fallbackTab.session,
          slideId: firstSlideId,
          slideIndex: 0,
          slideNumber: firstSlideNumber,
          name: `Slide ${firstSlideNumber}`,
          updatedAt: Date.now(),
        },
        isLoading: false,
        loadingStatus: "",
        streamingThinking: "",
        visionFeedback: "",
        refinementStep: 0,
      },
    ];
  }

  const nextActiveTabId = nextTabs.some((tab) => tab.id === prev.activeTabId)
    ? prev.activeTabId
    : nextTabs[0].id;

  const unchanged =
    prev.activeTabId === nextActiveTabId &&
    prev.tabs.length === nextTabs.length &&
    prev.tabs.every((tab, idx) => {
      const nextTab = nextTabs[idx];
      return (
        Boolean(nextTab) &&
        tab.id === nextTab.id &&
        tab.session.slideId === nextTab.session.slideId &&
        tab.session.slideIndex === nextTab.session.slideIndex &&
        tab.session.slideNumber === nextTab.session.slideNumber &&
        tab.session.name === nextTab.session.name
      );
    });

  if (unchanged) {
    return prev;
  }

  return {
    ...prev,
    tabs: nextTabs,
    activeTabId: nextActiveTabId,
  };
}

export function resolveTabClick(state: TabsState, tabId: string): TabClickResolution {
  const targetTab = state.tabs.find((tab) => tab.id === tabId);
  if (!targetTab) {
    return { nextState: state };
  }

  return {
    nextState: state.activeTabId === tabId ? state : { ...state, activeTabId: tabId },
    navigationTarget: {
      slideId: targetTab.session.slideId,
      slideIndex: targetTab.session.slideIndex,
    },
  };
}
